import {
    consentOriginStorage,
    consentReturnToStorage,
    navigateTo,
    pendingProjectConsentStorage,
    setupTenantIdStorage
} from "@sg-protect/core";
import { clearSetupStorage, parseConsentOrigin, useMigratePaths, useTenants } from "@sg-protect/shell";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { queryOptions } from "../api/client.ts";
import { type WorkspaceConsentStatusResponse, workspaceConsentStatusQueryOptions } from "../api/routes/auth.ts";
import { protectProfileQueryOptions } from "../api/routes/subscription.ts";
import { usePaths } from "../paths.ts";
import { READ_SUCCESS_STEP, isSetupSuccessful, resolveReturnTo } from "../utils/setupExit.ts";

export function useSetupExit() {
    const paths = usePaths();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { refetchTenants } = useTenants();
    const { root: migrateRoot } = useMigratePaths();
    const [searchParams] = useSearchParams();
    const isRemediationSuccess = searchParams.get("step") === "remediation" && !searchParams.has("error");

    const completeSetup = useCallback(() => {
        const returnTo = consentReturnToStorage.get();
        const isMigrateAssessment = parseConsentOrigin(consentOriginStorage.get()) === "migrateAssessment";
        queryClient.removeQueries({ queryKey: protectProfileQueryOptions().queryKey });
        if (isRemediationSuccess) {
            queryClient.removeQueries({
                queryKey: queryOptions("get", "/bff/remediation/consent/getStatus").queryKey
            });
        }
        if (isMigrateAssessment) {
            const consentedTenantId = setupTenantIdStorage.get();
            if (consentedTenantId) {
                pendingProjectConsentStorage.setTenantId(consentedTenantId);
            }
        }
        const target = resolveReturnTo(returnTo, paths.home);
        // The pages after setup read consent from the bootstrap's answer, which setup's consent has made stale.
        void refetchTenants().then(
            () => {
                clearSetupStorage();
                void navigate(target);
            },
            // Setup storage stays set: this page sends the setup tenant from it until the page load replaces it, and
            // the next setup entry clears it.
            () => navigateTo(target)
        );
    }, [queryClient, refetchTenants, isRemediationSuccess, paths.home, navigate]);

    const exitSetup = useCallback(() => {
        const returnTo = consentReturnToStorage.get();
        const isMigrateAssessment = parseConsentOrigin(consentOriginStorage.get()) === "migrateAssessment";
        const target = isMigrateAssessment ? migrateRoot : resolveReturnTo(returnTo, paths.home);

        clearSetupStorage();
        void navigate(target, { replace: true });
    }, [migrateRoot, paths.home, navigate]);

    const closeSetup = useCallback(() => {
        // Several cache entries hold the consent status and only the Connected page's one is polled, so any
        // entry reporting consent counts.
        const isConsented = queryClient
            .getQueriesData<WorkspaceConsentStatusResponse>({ queryKey: workspaceConsentStatusQueryOptions().queryKey })
            .some(([, data]) => data?.isConsented);
        const isReadSuccess = searchParams.get("step") === READ_SUCCESS_STEP && !searchParams.has("error");

        if (isReadSuccess || isSetupSuccessful(searchParams, isConsented)) {
            completeSetup();
        } else {
            exitSetup();
        }
    }, [queryClient, searchParams, completeSetup, exitSetup]);

    return { closeSetup, completeSetup, exitSetup };
}
